import React, { useState, useRef, useCallback } from "react";
import { Spinner } from "@chakra-ui/react";
import {
  callWithAuth,
  putUserPhraseStatus,
  deleteUserPhrase,
} from "../../api/Api";
import { GetDictionaryStatsResponse, UserPhrase } from "@shared_types";
import { useQueryClient } from "@tanstack/react-query";
import { useUserPhrases } from "@/hooks/useUserPhrases";
import { useTrackDictionaryEvents } from "@/hooks/posthog/useTrackDictionaryEvents";
import PhraseCard from "./PhraseCard";
import DictionaryEmptyState, { EMPTY_STATES } from "./DictionaryEmptyState";

interface SegmentedPhrasesViewProps {
  viewMode: "known" | "learning";
  searchQuery: string;
  onPhraseStatusUpdate: () => void;
  phraseQueryKeys: {
    knownPhrasesKey: string;
    learningPhrasesKey: string;
  };
  onCountsChange?: (counts: { known: number; learning: number }) => void;
}

export interface SegmentedPhrasesViewHandle {
  refreshQueries: () => void;
}

const PAGE_SIZE = 20;

const SegmentedPhrasesView = React.forwardRef<
  SegmentedPhrasesViewHandle,
  SegmentedPhrasesViewProps
>(
  (
    {
      viewMode,
      searchQuery,
      onPhraseStatusUpdate,
      phraseQueryKeys,
      onCountsChange,
    },
    ref
  ) => {
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [pendingRefetch, setPendingRefetch] = useState(false);
    const [modifiedPhrases, setModifiedPhrases] = useState<
      Record<number, "known" | "learning">
    >({});
    // Optimistically removed phrase ids: hide them immediately on delete, restore
    // on API failure (mirrors iOS handleDeletePhrase).
    const [deletedPhraseIds, setDeletedPhraseIds] = useState<Set<number>>(
      new Set()
    );

    const isSearching = searchQuery.length > 0;
    const queryClient = useQueryClient();
    const {
      trackPhraseMovedToKnown,
      trackPhraseMovedToLearning,
      trackPhraseDeleted,
    } = useTrackDictionaryEvents();

    const observerRef = useRef<IntersectionObserver | null>(null);
    const lastPhraseElementRef = useRef<HTMLDivElement | null>(null);

    const {
      activeQuery,
      activeViewPhrases: phrasesDisplaying,
      knownCount,
      learningCount,
      fetchNextPage,
      refetchAllQueries,
      isLoading,
      isFetchingNextPage,
    } = useUserPhrases({
      viewMode,
      searchQuery,
      pageSize: PAGE_SIZE,
      phraseQueryKeys,
      isSearching,
    });

    React.useEffect(() => {
      onCountsChange?.({ known: knownCount, learning: learningCount });
    }, [knownCount, learningCount, onCountsChange]);

    React.useImperativeHandle(ref, () => ({
      refreshQueries: () => {
        handleRefetchQueries();
      },
    }));

    const handleRefetchQueries = () => {
      refetchAllQueries();
      setPendingRefetch(false);
      setModifiedPhrases({});
      setDeletedPhraseIds(new Set());
    };

    React.useEffect(() => {
      if (pendingRefetch) {
        handleRefetchQueries();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode]);

    const lastPhraseRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (observerRef.current) observerRef.current.disconnect();
        lastPhraseElementRef.current = node;
        if (!node) return;
        observerRef.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            if (activeQuery.hasNextPage && !activeQuery.isFetchingNextPage) {
              fetchNextPage();
            }
          }
        });
        observerRef.current.observe(node);
      },
      [activeQuery, fetchNextPage]
    );

    const toggleKnownStatus = async (
      userPhrase: UserPhrase,
      newStatus: "known" | "learning"
    ) => {
      try {
        setModifiedPhrases((prev) => ({
          ...prev,
          [userPhrase.id]: newStatus,
        }));
        if (newStatus === "known") {
          trackPhraseMovedToKnown(
            userPhrase.phraseId.toString(),
            userPhrase.text,
            "dictionary",
          );
        } else {
          trackPhraseMovedToLearning(
            userPhrase.phraseId.toString(),
            userPhrase.text,
            "dictionary",
          );
        }
        await callWithAuth(putUserPhraseStatus, {
          phraseId: userPhrase.phraseId,
          phraseStatus: newStatus,
        });
        setPendingRefetch(true);
        queryClient.setQueryData(
          ["dictionaryStats"],
          (oldData: GetDictionaryStatsResponse | undefined) => {
          if (!oldData) return oldData;
          const newData = { ...oldData };
          if (newStatus === "known") {
            newData.knownPhraseCount = (oldData.knownPhraseCount || 0) + 1;
            newData.learningPhraseCount = Math.max(
              0,
              (oldData.learningPhraseCount || 0) - 1
            );
          } else {
            newData.knownPhraseCount = Math.max(
              0,
              (oldData.knownPhraseCount || 0) - 1
            );
            newData.learningPhraseCount =
              (oldData.learningPhraseCount || 0) + 1;
          }
          return newData;
        });
        onPhraseStatusUpdate();
      } catch (error) {
        // iOS reverts the phrase to its original status on error (Dictionary.tsx
        // handleStatusChange phrase catch). Drop the optimistic override so the
        // card falls back to phrase.phraseStatus.
        setModifiedPhrases((prev) => {
          const newModified = { ...prev };
          delete newModified[userPhrase.id];
          return newModified;
        });
        console.error("Error updating phrase status:", error);
      }
    };

    const handleDeleteClick = async (userPhrase: UserPhrase) => {
      if (deleteConfirmId === userPhrase.id) {
        // iOS removes the phrase from the list immediately and restores it if
        // the API fails (Dictionary.tsx handleDeletePhrase).
        setDeletedPhraseIds((prev) => new Set(prev).add(userPhrase.id));
        setDeleteConfirmId(null);
        try {
          trackPhraseDeleted(
            userPhrase.phraseId.toString(),
            userPhrase.text,
            "dictionary",
          );
          await callWithAuth(deleteUserPhrase, {
            phraseId: userPhrase.phraseId,
          });
          refetchAllQueries();
          setModifiedPhrases((prev) => {
            const newModified = { ...prev };
            delete newModified[userPhrase.id];
            return newModified;
          });
          onPhraseStatusUpdate();
        } catch (error) {
          setDeletedPhraseIds((prev) => {
            const next = new Set(prev);
            next.delete(userPhrase.id);
            return next;
          });
          console.error("Error deleting phrase:", error);
        }
      } else {
        setDeleteConfirmId(userPhrase.id);
      }
    };

    const handleCancelDelete = () => {
      setDeleteConfirmId(null);
    };

    const visiblePhrases = phrasesDisplaying.filter(
      (phrase) => !deletedPhraseIds.has(phrase.id)
    );
    const emptyConfig =
      viewMode === "known"
        ? EMPTY_STATES["phrases-known"]
        : EMPTY_STATES["phrases-learning"];

    return (
      <div className="min-h-[50vh]">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center w-full h-64 p-8">
            <Spinner size="xl" color="var(--color-green)" thickness="4px" />
            <div className="mt-4 text-base font-normal text-gray-400">
              Loading phrases...
            </div>
          </div>
        ) : visiblePhrases.length === 0 ? (
          <DictionaryEmptyState {...emptyConfig} />
        ) : (
          <div className="flex flex-col gap-4">
            {visiblePhrases.map((phrase, index) => {
              const effectiveStatus =
                modifiedPhrases[phrase.id] || phrase.phraseStatus;
              return (
                <PhraseCard
                  viewType={viewMode}
                  key={phrase.id}
                  phrase={phrase}
                  effectiveStatus={effectiveStatus}
                  deleteConfirmId={deleteConfirmId}
                  onStatusChange={toggleKnownStatus}
                  onDeleteClick={handleDeleteClick}
                  onCancelDelete={handleCancelDelete}
                  lastPhraseRef={
                    index === Math.max(0, visiblePhrases.length - 10)
                      ? lastPhraseRef
                      : null
                  }
                />
              );
            })}

            {isFetchingNextPage && (
              <div className="flex justify-center my-4">
                <Spinner size="md" color="var(--color-green)" />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

export default SegmentedPhrasesView;
